import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Help as UnknownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { monitoringApi } from '../../services/api';

const getStatusIcon = (status, hasError) => {
  if (hasError || status === 'failed' || status === 'error') {
    return <ErrorIcon sx={{ color: 'error.main', fontSize: 40 }} />;
  }
  if (status === 'warning') {
    return <WarningIcon sx={{ color: 'warning.main', fontSize: 40 }} />;
  }
  if (status === 'success') {
    return <SuccessIcon sx={{ color: 'success.main', fontSize: 40 }} />;
  }
  return <UnknownIcon sx={{ color: 'grey.500', fontSize: 40 }} />;
};

const getStatusColor = (status, hasError) => {
  if (hasError || status === 'failed' || status === 'error') return 'error';
  if (status === 'warning') return 'warning';
  if (status === 'success') return 'success';
  return 'default';
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return 'Never';

  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString();
};

const formatTimestamp = (isoString) => {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleString();
};

const PipelineCard = ({ data, showFilingDetails, show8KDetails }) => {
  const [expanded, setExpanded] = useState(false);
  const statusColor = getStatusColor(data.status, data.has_error);

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        borderLeft: 4,
        borderColor: `${statusColor}.main`,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {data.pipeline}
            </Typography>
            <Chip
              label={data.status?.toUpperCase() || 'UNKNOWN'}
              color={statusColor}
              size="small"
              sx={{ mb: 2 }}
            />
          </Box>
          {getStatusIcon(data.status, data.has_error)}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Last Activity
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {formatRelativeTime(data.last_updated)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(data.last_updated)}
          </Typography>
        </Box>

        {/* RSS Monitor / Filing ingestion details */}
        {showFilingDetails && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Filings (24h)
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data.filings_last_24h ?? 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Filings (1h)
                </Typography>
                <Typography variant="body1" fontWeight="medium" color={data.filings_last_hour > 0 ? 'success.main' : 'text.primary'}>
                  {data.filings_last_hour ?? 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* 8-K Monitor details */}
        {show8KDetails && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  8-Ks Processed (24h)
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data.processed_last_24h ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  CRLs Detected (24h)
                </Typography>
                <Typography variant="body1" fontWeight="medium" color={data.crls_detected_24h > 0 ? 'success.main' : 'text.primary'}>
                  {data.crls_detected_24h ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  CRLs Detected (7d)
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data.crls_detected_7d ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Awaiting Confirmation
                </Typography>
                <Typography variant="body1" fontWeight="medium" color={data.unconfirmed_crls > 0 ? 'warning.main' : 'text.primary'}>
                  {data.unconfirmed_crls ?? 0}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Standard records processed (for non-filing cards) */}
        {!showFilingDetails && !show8KDetails && data.records_processed !== undefined && data.records_processed !== null && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Last 7 Days
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {data.records_processed.toLocaleString()} predictions
            </Typography>
          </Box>
        )}

        {data.error_message && (
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setExpanded(!expanded)}
            >
              <Typography variant="body2" color={data.has_error ? 'error.main' : 'warning.main'} sx={{ fontWeight: 'medium' }}>
                {data.has_error ? 'Error Details' : 'Note'}
              </Typography>
              <IconButton size="small">
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={expanded}>
              <Alert severity={data.has_error ? 'error' : 'warning'} sx={{ mt: 1 }}>
                {data.error_message}
              </Alert>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const PipelineStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await monitoringApi.getPipelineStatus();
      setStatus(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error loading pipeline status:', err);
      setError(err.response?.data?.error || 'Failed to load pipeline status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();

    // Auto-refresh every 60 seconds
    const interval = setInterval(loadStatus, 60000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Pipeline Status
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor data pipeline health for CRL and Insider Trading modules
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {lastRefresh && (
              <Typography variant="caption" color="text.secondary">
                Last checked: {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={loadStatus} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && !status ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : status ? (
          <>
            {/* CRL Section */}
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              FDA CRL Analysis
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6} lg={4}>
                <PipelineCard data={status.crl} />
              </Grid>
              {status.eight_k_monitor && (
                <Grid item xs={12} md={6} lg={4}>
                  <PipelineCard data={status.eight_k_monitor} show8KDetails />
                </Grid>
              )}
            </Grid>

            {/* Insider Trading Section */}
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Insider Trading
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={4}>
                <PipelineCard data={status.insider_rss} showFilingDetails />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <PipelineCard data={status.insider_ml} />
              </Grid>
            </Grid>
          </>
        ) : null}

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Pipeline Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>CRL Letters:</strong> Polls OpenFDA API daily at 8 AM for new Complete Response Letters.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>8-K CRL Monitor:</strong> Monitors SEC 8-K filings from pharma companies to detect CRLs early,
            before they appear in the FDA database. Detected CRLs are marked as "unconfirmed" until confirmed by FDA.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>RSS Monitor:</strong> Polls SEC EDGAR RSS feed every minute during market hours for new Form 4 filings
            from pharmaceutical companies. Shows when feed was last checked and filing counts.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>ML Predictions:</strong> Generates trading signals when new pharma filings are found.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default PipelineStatus;
